import React, { useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FaArrowRight } from "react-icons/fa6";

gsap.registerPlugin(ScrollTrigger);

const CaseStudySlider = ({ caseStudies }) => {
  const caseStudySectionRef = useRef(null);
  const caseStudyHeadingRef = useRef(null);
  const animationTriggered = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !animationTriggered.current) {
            animationTriggered.current = true;
            animateCaseStudySlider();
            if (caseStudySectionRef.current) {
              observer.unobserve(caseStudySectionRef.current);
            }
          }
        });
      },
      {
        threshold: 0.2,
      }
    );

    const currentRef = caseStudySectionRef.current;

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const animateCaseStudySlider = () => {
    const heading = caseStudyHeadingRef.current;
    // Note: Swiper duplicates slides for looping, so we target generalized classes
    const swiperSlides = caseStudySectionRef.current.querySelectorAll('.swiper-slide');

    const tl = gsap.timeline({ defaults: { duration: 0.8, ease: 'power2.out' } });

    tl.fromTo(
      heading,
      { yPercent: -20, opacity: 0 },
      { yPercent: 0, opacity: 1 }
    );

    tl.fromTo(
      swiperSlides,
      { yPercent: 20, opacity: 0 },
      { yPercent: 0, opacity: 1, stagger: 0.15 },
      '-=0.5'
    );
  };

  return (
    <div className="bg-black py-12" ref={caseStudySectionRef}>
        <div className=" text-center md:text-left mb-10 md:mb-12" ref={caseStudyHeadingRef}>
          <h2 className="font-[600] text-white text-[38px] sm:text-[52px] mb-24 text-center">Case Studies</h2>
        </div>
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          spaceBetween={50}
          slidesPerView={1}
          loop={true}
          autoplay={{
            delay: 3000,
          }}
          breakpoints={{
            640: {
              slidesPerView: 1,
              spaceBetween: 50,
            },
            1028: {
              slidesPerView: 2,
              spaceBetween: 50,
            }
          }}
          // FIX 1: Ensure Swiper wrapper treats slides as equal height columns
          className='h-full !pb-12' 
        >
          {caseStudies.map((study, index) => (
            // FIX 2: Use !h-auto so the slide stretches to match the tallest sibling
            <SwiperSlide key={index} className='!h-auto'>
              
              {/* FIX 3: h-full ensures the article takes up the full height of the stretched slide */}
              <article className="flex flex-col w-full bg-white rounded-xl shadow-lg overflow-hidden h-full">
                
                {/* Image Section (Fixed height) */}
                <figure className="w-full h-[250px] relative flex justify-center items-center overflow-hidden bg-white p-5 shrink-0">
                  <img
                    src={study.image}
                    alt="Case Study"
                    className="w-full h-full object-contain"
                  />
                </figure>

                {/* FIX 4: flex-1 forces this blue section to grow and fill empty space */}
                <figcaption className="w-full bg-[#0D3BD1] p-7 min-[640px]:p-14 flex-1 flex flex-col">
                  <h2 className='text-white text-[24px] mb-8'>
                    <span className='font-[700]'>{study.heading.bold}</span>
                    <span className='font-[500] break-words ml-2'>{study.heading.light}</span>
                  </h2>
                  
                  {/* mt-auto pushes the list to the bottom if you wanted, 
                      or just let it flow naturally with the expanded blue background */}
                  <ol className='flex flex-col gap-y-7'>
                    {study.content.map((content , index) => (
                      <li key={index} className='flex flex-col gap-y-5'>
                        <h4 className='flex items-center gap-x-3 text-white'>
                          <span className={`p-1 rounded-full ${index === 0 ? 'bg-orange-600' : index === 1 ? 'bg-green-500' : 'bg-yellow-500'}`}>
                            <FaArrowRight className='text-white text-[10px]' />
                          </span>
                          <span className='text-[17px] font-[700]'>{content.title}</span>
                        </h4>
                        <p className='text-white font-[400] text-[16px]'>{content.description}</p>
                      </li>
                    ))}
                  </ol>
                </figcaption>

              </article>
            </SwiperSlide>
          ))}
        </Swiper>
    </div>
  );
};

export default CaseStudySlider;